import { formatInTimeZone } from 'date-fns-tz';
import { format, parseISO } from 'date-fns';

/**
 * Format a date in the user's timezone
 * Falls back to browser timezone if no user timezone provided
 */
export const formatDateInTimezone = (
  date: Date | string | null | undefined,
  formatString: string,
  userTimezone?: string | null
): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const timezone = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatInTimeZone(dateObj, timezone, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fallback to basic format without timezone
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      return format(dateObj, formatString);
    } catch {
      return '';
    }
  }
};

/**
 * Get current date/time in user's timezone
 */
export const getCurrentDateInTimezone = (userTimezone?: string | null): Date => {
  // The Date object is always UTC internally, but we can use it with timezone formatting
  return new Date();
};

/**
 * Common date format presets
 */
export const DATE_FORMATS = {
  short: 'MMM d, yyyy',           // Jan 1, 2024
  long: 'MMMM d, yyyy',           // January 1, 2024
  withTime: "MMM d, yyyy 'at' h:mm a", // Jan 1, 2024 at 3:30 PM
  timeOnly: 'h:mm a',             // 3:30 PM
  dayMonth: 'MMM d',              // Jan 1
  full: 'EEEE, MMMM d, yyyy',     // Monday, January 1, 2024
  iso: 'yyyy-MM-dd',              // 2024-01-01
} as const;

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Get the next occurrence of a specific weekday
 */
export const getNextWeekday = (dayOfWeek: string): Date => {
  const targetDay = DAYS_OF_WEEK.indexOf(dayOfWeek);
  if (targetDay === -1) return new Date();
  
  const today = new Date();
  const currentDay = today.getDay();
  let daysUntilTarget = targetDay - currentDay;
  
  // If today is the target day or we've passed it, go to next week
  if (daysUntilTarget <= 0) {
    daysUntilTarget += 7;
  }
  
  const result = new Date(today);
  result.setDate(today.getDate() + daysUntilTarget);
  return result;
};

/**
 * Get the next occurrence of a pattern like "First Friday of the month"
 */
export const getNextPatternDate = (
  occurrence: 'First' | 'Second' | 'Third' | 'Fourth' | 'Last',
  dayOfWeek: string
): Date => {
  const targetDay = DAYS_OF_WEEK.indexOf(dayOfWeek);
  if (targetDay === -1) return new Date();
  
  const today = new Date();
  let month = today.getMonth();
  let year = today.getFullYear();
  
  // Try current month first, then next month
  for (let attempt = 0; attempt < 2; attempt++) {
    const result = getOccurrenceInMonth(year, month, occurrence, targetDay);
    if (result > today) {
      return result;
    }
    // Move to next month
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }
  
  // Fallback - should never reach here
  return getOccurrenceInMonth(year, month, occurrence, targetDay);
};

/**
 * Get a specific occurrence of a weekday in a given month
 */
const getOccurrenceInMonth = (
  year: number,
  month: number,
  occurrence: 'First' | 'Second' | 'Third' | 'Fourth' | 'Last',
  dayOfWeek: number
): Date => {
  if (occurrence === 'Last') {
    // Start from the last day of the month and work backwards
    const lastDay = new Date(year, month + 1, 0);
    const lastDayOfWeek = lastDay.getDay();
    let diff = lastDayOfWeek - dayOfWeek;
    if (diff < 0) diff += 7;
    return new Date(year, month + 1, -diff);
  }
  
  // For First, Second, Third, Fourth
  const occurrenceMap: Record<string, number> = {
    'First': 1,
    'Second': 2,
    'Third': 3,
    'Fourth': 4,
  };
  const n = occurrenceMap[occurrence] || 1;
  
  // Find the first occurrence of the weekday in the month
  const firstOfMonth = new Date(year, month, 1);
  const firstDayOfWeek = firstOfMonth.getDay();
  let diff = dayOfWeek - firstDayOfWeek;
  if (diff < 0) diff += 7;
  
  // Calculate the date of the nth occurrence
  const date = 1 + diff + (n - 1) * 7;
  return new Date(year, month, date);
};

/**
 * Format a meeting pattern for display
 */
export const formatMeetingPattern = (
  frequency: string,
  occurrence?: string,
  dayOfWeek?: string
): { description: string; nextDate: Date | null } => {
  if (!frequency) return { description: '', nextDate: null };
  
  if (frequency === 'Weekly' && dayOfWeek) {
    const nextDate = getNextWeekday(dayOfWeek);
    return {
      description: `Every ${dayOfWeek}`,
      nextDate,
    };
  }
  
  if (frequency === 'Bi-Weekly' && dayOfWeek) {
    const nextDate = getNextWeekday(dayOfWeek);
    return {
      description: `Every other ${dayOfWeek}`,
      nextDate,
    };
  }
  
  if (frequency === 'Monthly' && occurrence && dayOfWeek) {
    const nextDate = getNextPatternDate(occurrence as 'First' | 'Second' | 'Third' | 'Fourth' | 'Last', dayOfWeek);
    return {
      description: `${occurrence} ${dayOfWeek} of every month`,
      nextDate,
    };
  }
  
  if (frequency === 'Quarterly') {
    return { description: 'Every quarter', nextDate: null };
  }
  
  if (frequency === 'Annually') {
    return { description: 'Once a year', nextDate: null };
  }
  
  return { description: '', nextDate: null };
};
